/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { breakpoint } from '../../../constants/breakpoint';
import { useViewport } from '../../../context/viewport.context';

const Video = ({ blok }) => {
  const videoRef = useRef(null);
  const { viewWidth } = useViewport();

  const videoUrlMp4 = useMemo(() => {
    // mobile
    if (viewWidth < breakpoint.sm) {
      return (
        _.get(blok, 'assetMobile_mp4.filename') ||
        _.get(blok, 'assetTablet_mp4.filename') ||
        _.get(blok, 'assetLaptop_mp4.filename') ||
        _.get(blok, 'assetDesktop_mp4.filename') ||
        null
      );
    }

    // tablet
    if (viewWidth < breakpoint.md) {
      return (
        _.get(blok, 'assetTablet_mp4.filename') ||
        _.get(blok, 'assetLaptop_mp4.filename') ||
        _.get(blok, 'assetDesktop_mp4.filename') ||
        null
      );
    }

    // laptop
    if (viewWidth < breakpoint.lg) {
      return _.get(blok, 'assetLaptop_mp4.filename') || _.get(blok, 'assetDesktop_mp4.filename') || null;
    }

    // desktop
    return _.get(blok, 'assetDesktop_mp4.filename', null);
  }, [blok, viewWidth]);

  const videoUrlWebm = useMemo(() => {
    // mobile
    if (viewWidth < breakpoint.sm) {
      return (
        _.get(blok, 'assetMobile_webm.filename') ||
        _.get(blok, 'assetTablet_webm.filename') ||
        _.get(blok, 'assetLaptop_webm.filename') ||
        _.get(blok, 'assetDesktop_webm.filename') ||
        null
      );
    }

    // tablet
    if (viewWidth < breakpoint.md) {
      return (
        _.get(blok, 'assetTablet_webm.filename') ||
        _.get(blok, 'assetLaptop_webm.filename') ||
        _.get(blok, 'assetDesktop_webm.filename') ||
        null
      );
    }

    // laptop
    if (viewWidth < breakpoint.lg) {
      return _.get(blok, 'assetLaptop_webm.filename') || _.get(blok, 'assetDesktop_webm.filename') || null;
    }

    // desktop
    return _.get(blok, 'assetDesktop_webm.filename', null);
  }, [blok, viewWidth]);

  const blokStyles = useMemo(() => {
    const styles = {};

    if (_.get(blok, 'maxHeight')) {
      styles.maxHeight = `${_.get(blok, 'maxHeight').toString()}px`;
    }

    if (_.get(blok, 'objectFit')) {
      styles.objectFit = _.get(blok, 'objectFit');
    }

    return styles;
  }, [blok]);

  // workaround to https://github.com/facebook/react/issues/10389
  useEffect(() => {
    if (videoRef.current && blok.autoplay) {
      videoRef.current.defaultMuted = true;
      videoRef.current.muted = true;
    }
  }, [blok, videoRef]);

  return (
    <div {...storyblokEditable(blok)} className="w-full flex justify-center items-center p-0">
      {(videoUrlMp4 || videoUrlWebm) && (
        <video
          autoPlay={blok.autoplay ?? false}
          className="w-full bg-cover bg-center bg-no-repeat"
          controls={blok.videoControls ?? false}
          loop={blok.loop ?? false}
          muted={blok.autoplay ?? false}
          playsInline
          poster={_.get(blok, 'posterImage.filename', '')}
          ref={videoRef}
          style={blokStyles}
        >
          {videoUrlMp4 && <source src={videoUrlMp4} type="video/mp4" />}

          {videoUrlWebm && <source src={videoUrlWebm} type="video/webm" />}
        </video>
      )}
    </div>
  );
};

export default React.memo(Video);
